import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Mail, PartyPopper } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

interface EmailGateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  url: string;
}

type ModalState = "email" | "loading" | "waitlist";

const EmailGateModal = ({ open, onOpenChange, url }: EmailGateModalProps) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<ModalState>("email");
  const [error, setError] = useState<string | null>(null);
  const [waitlistJoined, setWaitlistJoined] = useState(false);
  const [gdprConsent, setGdprConsent] = useState(true);
  const [joinWaitlist, setJoinWaitlist] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    if (!gdprConsent) {
      toast({
        title: "Consent required",
        description: "You must agree to the processing of your data to continue.",
        variant: "destructive",
      });
      return;
    }
    setState("loading");
    setError(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke("check-usage", {
        body: { email: email.trim().toLowerCase() },
      });

      if (fnError) throw new Error(fnError.message);

      if (data.canAnalyze) {
        if (joinWaitlist) {
          void supabase.functions.invoke("join-waitlist", {
            body: { email: email.trim().toLowerCase() },
          });
        }
        const encodedUrl = encodeURIComponent(url);
        const encodedEmail = encodeURIComponent(email.trim().toLowerCase());
        navigate(`/analyze?url=${encodedUrl}&email=${encodedEmail}`);
      } else {
        setWaitlistJoined(data.onWaitlist);
        setState("waitlist");
      }
    } catch (err: any) {
      setError(err.message || "Something went wrong");
      setState("email");
    }
  };

  const handleJoinWaitlist = async () => {
    try {
      const { error: fnError } = await supabase.functions.invoke("join-waitlist", {
        body: { email: email.trim().toLowerCase() },
      });
      if (fnError) throw fnError;
      setWaitlistJoined(true);
    } catch {
      // Even if it fails, navigate to waitlist page
    }
    navigate("/waitlist");
  };

  const handleClose = (value: boolean) => {
    if (!value) {
      setState("email");
      setError(null);
    }
    onOpenChange(value);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        {state === "email" || state === "loading" ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-primary" />
                Get Your Free Analysis
              </DialogTitle>
              <DialogDescription>
                Enter your email to receive a free SEO & AI visibility audit.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label htmlFor="gate-email">Email address</Label>
                <Input
                  id="gate-email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={state === "loading"}
                />
              </div>
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="waitlist-gate"
                  checked={joinWaitlist}
                  onCheckedChange={(checked) => setJoinWaitlist(checked === true)}
                  disabled={state === "loading"}
                />
                <label htmlFor="waitlist-gate" className="text-xs text-muted-foreground leading-tight cursor-pointer">
                  Join the waitlist to get early access when we launch
                </label>
              </div>
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="gdpr-gate"
                  checked={gdprConsent}
                  onCheckedChange={(checked) => setGdprConsent(checked === true)}
                  disabled={state === "loading"}
                />
                <label htmlFor="gdpr-gate" className="text-xs text-muted-foreground leading-tight cursor-pointer">
                  I agree to the processing of my data in accordance with GDPR
                </label>
              </div>
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" className="w-full" disabled={state === "loading"}>
                {state === "loading" ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Checking…
                  </>
                ) : (
                  "Start Free Analysis"
                )}
              </Button>
              <p className="text-xs text-muted-foreground text-center">
                We'll only use your email to notify you about our launch.
              </p>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <PartyPopper className="w-5 h-5 text-primary" />
                You've Used Your Free Analysis
              </DialogTitle>
              <DialogDescription>
                Join our waitlist to be the first to know when we launch with full access.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              {!waitlistJoined ? (
                <Button onClick={handleJoinWaitlist} className="w-full">
                  Join the Waitlist
                </Button>
              ) : (
                <p className="text-sm text-muted-foreground text-center">
                  You're already on the waitlist! We'll be in touch.
                </p>
              )}
              <Button variant="outline" className="w-full" onClick={() => handleClose(false)}>
                Close
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmailGateModal;
